/** @jsxImportSource @emotion/react */
import { Menu, MenuItem, Theme, useTheme } from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import React, { useEffect, useState } from 'react';
import { StylesClassName, UseStyles } from 'types/styles';
import { useLang } from 'contexts/intl';
import { getLanguagePathname } from 'application';
import { langs } from 'utils/langs';

interface ILangsDropdownProps {
  className?: StylesClassName;
}

const supportedLanguageCodes = ['en'];

const LangsDropdown: React.FC<ILangsDropdownProps> = ({ className }) => {
  const theme = useTheme() as Theme;
  const { lang: selectedLang, dir: selectedDir } = useLang();
  const classes = useStyles(theme);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    document.dir = selectedDir;
    document.documentElement.setAttribute('lang', selectedLang);
  }, [selectedDir, selectedLang]);

  return (
    <div css={[classes.LangsDropdown, className]}>
      <button css={classes.btn} onClick={handleClick}>
        <TranslateIcon css={[classes.translateIcon, open ? classes.hoverTranslateIcon : '']} className="langsIcon" />
        <ArrowDropDownIcon css={open ? classes.arrowIcon : ''} />
      </button>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {langs.map(lang => (
          <MenuItem
            css={!supportedLanguageCodes.includes(lang.code) ? classes.disabled : undefined}
            component={'a'}
            href={getLanguagePathname(lang.code)}
            key={lang.code}
            selected={lang.code === selectedLang}
          >
            {lang.name}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

const useStyles = (theme: Theme): UseStyles => ({
  LangsDropdown: {},
  btn: {
    background: 'none',
    border: 0,
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',

    svg: {
      path: {
        fill: theme.palette.common.white,
      },
    },
    '&:hover': {
      '.langsIcon': {
        path: {
          fill: theme.palette.custom.primary.brand[20],
        },
      },
    },
  },
  translateIcon: {
    width: 24,
    height: 24,
  },
  hoverTranslateIcon: {
    path: {
      fill: `${theme.palette.custom.primary.brand[20]} !important`,
    },
  },
  arrowIcon: {
    transform: 'rotate(180deg)',
  },
  selected: {},
  disabled: {
    opacity: 0.4,
    pointerEvents: 'none',
  },
});

export default LangsDropdown;
